import { useCallback, useEffect, useRef, useState } from "react";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll.js";
import ProductCatalogGrid from "@/components/ProductCatalogGrid.jsx";
import ProductRecommendations from "@/components/ProductRecommendations.jsx";
import { searchNewlyPublishedProducts, searchRestockedProducts } from "@/api/public.api.js";
import "@/components/product-filter.css";
import "@/pages/home/home.css";
import "./catalog-section.css";

const FETCHERS = {
    new: searchNewlyPublishedProducts,
    restocked: searchRestockedProducts
};

const TITLES = {
    new: "Nouveaux articles",
    restocked: "Articles restockés"
};

const DESCRIPTIONS = {
    new: "Pièces récemment ajoutées au catalogue — découvrez ce qui vient d'arriver.",
    restocked: "Pièces de nouveau disponibles en stock après une rupture — reprenez-les avant qu'elles ne finissent."
};

const EMPTY_MESSAGES = {
    new: "Aucun nouvel article pour le moment.",
    restocked: "Aucun article restocké pour le moment."
};

/**
 * Catalogue-style section for newly published or restocked products.
 * Order uses recommendation-style ranking within the highlight window; seed keeps scroll stable.
 * @param {{ kind: "new" | "restocked" }} props
 */
export default function CatalogSectionContainer({ kind }) {
    const fetchPageApi = FETCHERS[kind];
    const title = TITLES[kind];
    const description = DESCRIPTIONS[kind];
    const emptyMessage = EMPTY_MESSAGES[kind];

    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const kindRef = useRef(kind);
    const seedRef = useRef(null);
    kindRef.current = kind;

    const fetchPage = useCallback(async (pageToLoad, append, seedForRequest) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError("");
        try {
            const response = await fetchPageApi(pageToLoad, seedForRequest || undefined);
            if (kindRef.current !== kind) {
                return;
            }
            const resolvedSeed = response.seed || seedForRequest;
            if (resolvedSeed) {
                seedRef.current = resolvedSeed;
            }
            setProducts((prev) => (append ? [...prev, ...(response.data || [])] : response.data || []));
            setHasNext(!!response.hasNext);
            setPage(pageToLoad);
        } catch (err) {
            if (kindRef.current !== kind) {
                return;
            }
            setError(err.message);
            if (!append) {
                setProducts([]);
                setHasNext(false);
            }
        } finally {
            if (kindRef.current === kind) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [fetchPageApi, kind]);

    useEffect(() => {
        setProducts([]);
        setPage(0);
        setHasNext(false);
        seedRef.current = null;
        fetchPage(0, false, null);
    }, [fetchPage]);

    const loadMore = useCallback(() => {
        fetchPage(page + 1, true, seedRef.current);
    }, [fetchPage, page]);

    const sentinelRef = useInfiniteScroll({ hasNext, loading, loadingMore, onLoadMore: loadMore });

    return (
        <main>
            <section className="container catalog-section">
                <div className="catalog-section-header">
                    <h1 className="catalog-section-title">{title}</h1>
                    <p className="catalog-section-description muted">{description}</p>
                </div>
                <div className="catalog-results" data-tour="catalog-results">
                    {loading && <p className="muted">Chargement des produits...</p>}
                    {error && <p className="error">{error}</p>}

                    {!loading && !error && products.length === 0 && (
                        <p className="muted">{emptyMessage}</p>
                    )}

                    <ProductCatalogGrid products={products} />

                    {loadingMore && <p className="muted catalog-loading-more">Chargement de la suite...</p>}
                    <div ref={sentinelRef} className="catalog-scroll-sentinel" aria-hidden="true" />
                </div>

                <ProductRecommendations className="catalog-section-recommendations" resetKey={kind} />
            </section>
        </main>
    );
}
